/**
 * Date utility functions for handling UTC to local time conversions
 */

/**
 * Debug function to test date conversion
 * @param {string} dateInput - UTC date string to test
 * @returns {Object} - Debug information
 */
export const debugDateConversion = (dateInput) => {
    console.group(`🐛 Debug Date Conversion: ${dateInput}`);
    
    try {
        // Test original input
        console.log('Original input:', dateInput);
        console.log('Input type:', typeof dateInput);
        
        // Test direct Date creation
        const directDate = new Date(dateInput);
        console.log('Direct new Date():', directDate);
        console.log('Direct toString():', directDate.toString());
        console.log('Direct toISOString():', directDate.toISOString());
        console.log('Direct toLocaleString():', directDate.toLocaleString());
        
        // Test our parsing function
        const parsedDate = parseUTCToLocal(dateInput);
        console.log('Our parseUTCToLocal():', parsedDate);
        
        if (parsedDate) {
            console.log('Parsed toString():', parsedDate.toString());
            console.log('Parsed toISOString():', parsedDate.toISOString());
            console.log('Parsed toLocaleString():', parsedDate.toLocaleString());
            
            // Test our formatting functions
            console.log('formatToLocalDate():', formatToLocalDate(dateInput));
            console.log('formatToLocalDateTime():', formatToLocalDateTime(dateInput));
            console.log('formatToLocalTime():', formatToLocalTime(dateInput));
        }
        
        // Test browser info
        console.log('Browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
        console.log('Browser locale:', getBrowserLocale());
        
    } catch (error) {
        console.error('Error in debug:', error);
    } finally {
        console.groupEnd();
    }
};

/**
 * Converts a UTC date string to a local Date object
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {Date|null} - Local Date object or null if invalid
 */
export const parseUTCToLocal = (dateInput) => {
    if (!dateInput) return null;
    
    try {
        let date;
        
        if (dateInput instanceof Date) {
            date = dateInput;
        } else if (typeof dateInput === 'string') {
            // Handle various UTC date string formats
            let dateStr = dateInput.trim();
            
            // If it's already in ISO format with Z, use it directly
            if (dateStr.endsWith('Z')) {
                date = new Date(dateStr);
            }
            // If it has timezone info like +00:00, replace with Z
            else if (dateStr.includes('+00:00')) {
                dateStr = dateStr.replace('+00:00', 'Z');
                date = new Date(dateStr);
            }
            // If it ends with ' UTC', replace with Z
            else if (dateStr.endsWith(' UTC')) {
                dateStr = dateStr.replace(' UTC', 'Z');
                date = new Date(dateStr);
            }
            // If it has space instead of T, fix the format and add Z
            else if (dateStr.includes(' ') && !dateStr.includes('T')) {
                dateStr = dateStr.replace(' ', 'T');
                if (!dateStr.endsWith('Z')) {
                    dateStr += 'Z';
                }
                date = new Date(dateStr);
            }
            // If it doesn't have timezone info, assume it's UTC and add Z
            else if (!dateStr.includes('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
                if (dateStr.includes('T')) {
                    dateStr = dateStr + 'Z';
                    date = new Date(dateStr);
                } else {
                    dateStr = dateStr + 'T00:00:00Z';
                    date = new Date(dateStr);
                }
            }
            // Otherwise, try parsing as-is
            else {
                date = new Date(dateStr);
            }
        } else if (typeof dateInput === 'number') {
            // Handle Unix timestamps (seconds or milliseconds)
            const timestamp = dateInput;
            // If timestamp is in seconds (< year 2100), convert to milliseconds
            date = new Date(timestamp > 4000000000 ? timestamp : timestamp * 1000);
        } else {
            return null;
        }
        
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            console.warn('❌ Invalid date result:', dateInput);
            return null;
        }
        
        return date;
    } catch (error) {
        console.error('❌ Error parsing date:', dateInput, error);
        return null;
    }
};

/**
 * Gets the user's browser locale, with fallback to 'en-US'
 * @returns {string} - Browser locale
 */
const getBrowserLocale = () => {
    return navigator.language || navigator.userLanguage || 'en-US';
};

/**
 * Formats a UTC date string to local date string
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted local date string
 */
export const formatToLocalDate = (dateInput, options = {}) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return 'No date';
    
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    };
    
    // Use user's browser locale instead of hardcoded 'en-US'
    return date.toLocaleDateString(getBrowserLocale(), { ...defaultOptions, ...options });
};

/**
 * The calendar day a date-only value names, as it was written.
 *
 * A birth date is a day on a calendar, not an instant: nobody was born at
 * midnight UTC. The server serves one as `"2000-05-14 00:00:00+00:00"`, and
 * every formatter above reads that as an instant and re-states it in the
 * reader's zone — which moves it to the 13th for anyone west of UTC. This one
 * takes the `YYYY-MM-DD` off the front and formats those three numbers, so the
 * day a candidate typed is the day they are shown, in Vancouver and in Tokyo.
 *
 * Anything that is not a date-only value falls through to `formatToLocalDate`,
 * which is the right reading for an instant.
 *
 * @param {string|Date} dateInput - Date-only value ("YYYY-MM-DD", optionally with a time)
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted calendar date
 */
export const formatCalendarDate = (dateInput, options = {}) => {
    const parts = calendarDateValue(dateInput);
    if (!parts) return formatToLocalDate(dateInput, options);

    const [year, month, day] = parts.split('-').map(Number);
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    };

    // Formatted in UTC against a UTC instant: the pair cancels out, so no zone
    // the reader happens to be in can shift the day.
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(
        getBrowserLocale(),
        { ...defaultOptions, ...options, timeZone: 'UTC' }
    );
};

/**
 * The same value as `YYYY-MM-DD`, which is what `<input type="date">` takes.
 * A value it cannot parse is dropped silently and the field renders empty, so
 * the trailing `" 00:00:00+00:00"` the server sends has to come off here.
 *
 * @param {string|Date} dateInput - Date-only value
 * @returns {string} - "YYYY-MM-DD", or "" when the input carries no calendar date
 */
export const calendarDateValue = (dateInput) => {
    if (!dateInput) return '';

    if (dateInput instanceof Date) {
        if (isNaN(dateInput.getTime())) return '';
        const month = String(dateInput.getMonth() + 1).padStart(2, '0');
        const day = String(dateInput.getDate()).padStart(2, '0');
        return `${dateInput.getFullYear()}-${month}-${day}`;
    }

    const match = String(dateInput).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
};

/**
 * Formats a UTC date string to local date and time string
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted local date and time string
 */
export const formatToLocalDateTime = (dateInput, options = {}) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return 'No date';
    
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    
    // Use toLocaleString for date and time together, with user's browser locale
    return date.toLocaleString(getBrowserLocale(), { ...defaultOptions, ...options });
};

/**
 * Formats a UTC date string to local time string only
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted local time string
 */
export const formatToLocalTime = (dateInput, options = {}) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return '';
    
    const defaultOptions = {
        hour: '2-digit',
        minute: '2-digit'
    };
    
    // Use user's browser locale instead of hardcoded 'en-US'
    return date.toLocaleTimeString(getBrowserLocale(), { ...defaultOptions, ...options });
};

/**
 * Formats a UTC date in an explicit IANA timezone rather than the browser's.
 * Test cycles publish the timezone their schedule is announced in, so every
 * candidate reads the same wall-clock time regardless of where they are.
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {string} timeZone - IANA timezone name, falls back to the browser's
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date and time string
 */
export const formatInTimeZone = (dateInput, timeZone, options = {}) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return '';

    const formatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...options
    };

    try {
        return date.toLocaleString(getBrowserLocale(), { ...formatOptions, timeZone });
    } catch {
        // An unknown timezone name throws; the browser's own zone is better than nothing.
        return date.toLocaleString(getBrowserLocale(), formatOptions);
    }
};

/**
 * The short label of a timezone at a given moment ("EDT", "GMT+2"), so a
 * formatted time can say which clock it is on.
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {string} timeZone - IANA timezone name, falls back to the browser's
 * @returns {string} - Short timezone label, empty when it cannot be resolved
 */
export const getTimeZoneLabel = (dateInput, timeZone) => {
    const date = parseUTCToLocal(dateInput) || new Date();

    try {
        const parts = new Intl.DateTimeFormat(getBrowserLocale(), {
            timeZone,
            timeZoneName: 'short'
        }).formatToParts(date);
        return parts.find(part => part.type === 'timeZoneName')?.value || '';
    } catch {
        return '';
    }
};

/**
 * Gets current local time as ISO string (for sending to server as UTC)
 * @returns {string} - Current time as UTC ISO string
 */
export const getCurrentUTCString = () => {
    return new Date().toISOString();
};

/**
 * Checks if a date is in the past (local time comparison)
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {boolean} - True if date is in the past
 */
export const isDateInPast = (dateInput) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return false;
    
    const now = new Date();
    return date < now;
};

/**
 * Gets relative time string (e.g., "2 hours ago", "in 3 minutes")
 * @param {string|Date} dateInput - UTC date string or Date object
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (dateInput) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return 'Unknown time';
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (Math.abs(diffInSeconds) < 60) {
        return 'Just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInSeconds > 0) {
        // Past
        if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
        } else if (diffInHours > 0) {
            return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
        } else {
            return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
        }
    } else {
        // Future
        const absDays = Math.abs(diffInDays);
        const absHours = Math.abs(diffInHours);
        const absMinutes = Math.abs(diffInMinutes);
        
        if (absDays > 0) {
            return `in ${absDays} day${absDays !== 1 ? 's' : ''}`;
        } else if (absHours > 0) {
            return `in ${absHours} hour${absHours !== 1 ? 's' : ''}`;
        } else {
            return `in ${absMinutes} minute${absMinutes !== 1 ? 's' : ''}`;
        }
    }
};

/**
 * Alternative formatting function with explicit English locale for consistency
 * Use this if you want to maintain English format regardless of user's browser locale
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted local date string in English
 */
export const formatToLocalDateEN = (dateInput, options = {}) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return 'No date';
    
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    };
    
    return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Alternative formatting function with explicit English locale for consistency
 * Use this if you want to maintain English format regardless of user's browser locale
 * @param {string|Date} dateInput - UTC date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted local date and time string in English
 */
export const formatToLocalDateTimeEN = (dateInput, options = {}) => {
    const date = parseUTCToLocal(dateInput);
    if (!date) return 'No date';
    
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    
    return date.toLocaleString('en-US', { ...defaultOptions, ...options });
}; 